import React, { useState, useEffect } from "react";
import { Form, Field } from "react-final-form";
import { Link } from "react-router-dom";
import {
  TextField,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Typography,
  Grid2,
  Alert,
} from "@mui/material"; // Import Checkbox, FormControlLabel, and CircularProgress
import { useApi } from "../../utils/api";
import moment from "moment";
import { useUserInfo } from "../../utils/user";
import LoadingButton from "@mui/lab/LoadingButton";

const AddClient = ({ onClose, onAddClient }) => {
  const { id, name, stripeAccountId, isAccountFullySetup } = useUserInfo();
  const {
    addClient,
    sendEmail,
    getProducts,
    postUserProducts,
    startConversation,
  } = useApi();
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [loading, setLoading] = useState(true); // Loading state
  const [apiLoading, setApiLoading] = useState(false);
  const [error, setError] = useState(null);
  const [missingDescriptionPrices, setMissingDescriptionPrices] = useState([]);
  const [missingQuantityPrices, setMissingQuantityPrices] = useState([]);
  useEffect(() => {
    const fetchProducts = async () => {
      const res = await getProducts(stripeAccountId);
      setProducts(res);
      const missingDescriptions = [];
      const missingQuantities = [];
      (res || []).forEach((product) => {
        product.prices.forEach((price) => {
          if (!price.metadata?.description) {
            missingDescriptions.push({
              productName: product.name,
              priceId: price.priceId,
            });
          }
          const quantityValue = price.metadata?.quantity;
          if (
            !price.recurring &&
            (quantityValue === undefined || quantityValue === "")
          ) {
            missingQuantities.push({
              productName: product.name,
              priceId: price.priceId,
            });
          }
        });
      });
      setMissingDescriptionPrices(missingDescriptions);
      setMissingQuantityPrices(missingQuantities);
      setLoading(false); // Set loading to false after fetching
    };
    if (isAccountFullySetup) {
      fetchProducts();
    } else {
      setLoading(false);
    }
  }, [getProducts, isAccountFullySetup]);

  const handleCheckboxChange = (priceId) => {
    setSelectedProducts((prevSelected) => {
      if (prevSelected.includes(priceId)) {
        return prevSelected.filter((id) => id !== priceId);
      } else {
        return [...prevSelected, priceId];
      }
    });
  };

  const onSubmit = async (v) => {
    if (!v.name || !v.email || !selectedProducts.length) {
      setError("Please fill in all fields");
      return;
    }
    setApiLoading("Creating Client...");
    try {
      const resp = await addClient({
        ...v,
        coachId: id,
        createdDate: moment().unix(),
      });
      if (!resp.id) {
        if (resp.message) {
          setError(resp.message);
        } else {
          setError("Failed to create client");
        }
        setApiLoading(false);
        return;
      }
      setApiLoading("Sending Email...");
      await sendEmail({
        emailType: "SET-PASSWORD",
        name: v.name,
        coachName: name,
        coachId: id,
        userId: btoa(v.email),
        sendEmail: v.email,
        resetLink: resp.resetLink,
      });

      setApiLoading("Assigning Products...");
      // Prepare items for postUserProducts
      const items = selectedProducts.map((priceId) => {
        const product = products.find((p) =>
          p.prices.some((price) => price.priceId === priceId)
        );
        return {
          productId: product.productId,
          priceId: priceId,
        };
      });

      // Submit selected products
      await postUserProducts({
        items,
        type: "assigned",
        userId: resp.id,
        stripeAccountId,
      });

      setApiLoading("Creating Conversation...");
      await startConversation({
        participantIds: [id, resp.id],
        name: v.name,
      });

      onClose();
      onAddClient();
      setApiLoading(false);
    } catch (error) {
      console.error("Failed to create client:", error);
      setError(
        error.message
          ? JSON.parse(error.message).message
          : "Failed to create client"
      );
      setApiLoading(false);
      return;
    }
  };

  // If no products are available, prompt to add products in Stripe instead of showing the form
  if (!loading && products.length === 0) {
    if (isAccountFullySetup) {
      return (
        <Dialog open fullWidth maxWidth="md">
          <DialogTitle>Add Client</DialogTitle>
          <DialogContent>
            <Alert severity="warning">
              No products found. Please add products in your Stripe Dashboard
              before adding clients.{" "}
            </Alert>
          </DialogContent>
        </Dialog>
      );
    }
    return (
      <Dialog open fullWidth maxWidth="md">
        <DialogTitle>Add Client</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            Account not fully setup. Please complete the setup process before
            adding clients.
          </Alert>
          <Button
            onClick={() => (window.location.href = "/coaching")}
            fullWidth
            variant="contained"
            sx={{ mt: 2 }}
          >
            Complete Setup
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open fullWidth maxWidth="md">
      <DialogTitle>Add Client</DialogTitle>
      {missingDescriptionPrices.length > 0 && (
        <Alert severity="error" sx={{ mx: 3, mb: 1 }}>
          Some prices are missing description metadata. Update them on the{" "}
          <Link to="/manage-products" style={{ color: "inherit" }}>
            Manage Products
          </Link>{" "}
          page before assigning.
        </Alert>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      <Form
        onSubmit={onSubmit}
        render={({ handleSubmit }) => (
          <>
            <DialogContent>
              <Grid2 width="50%">
                <Field
                  name="name"
                  render={({ input }) => (
                    <TextField
                      label="Name"
                      size="small"
                      sx={{ mb: 2, mt: 1 }}
                      fullWidth
                      {...input}
                    />
                  )}
                />
                <Field
                  name="email"
                  render={({ input }) => (
                    <TextField
                      label="Email"
                      size="small"
                      fullWidth
                      {...input}
                    />
                  )}
                />
              </Grid2>
              <h3>Select Products</h3>
              {loading ? (
                <CircularProgress width="100%" /> // Show loader while fetching products
              ) : (
                <>
                  {products.map((product) => (
                    <div key={product.productId}>
                      <h4>{product.name}</h4>
                      {product.prices.map((price) => (
                        <FormControlLabel
                          key={price.priceId}
                          sx={{
                            width: "100%",
                            margin: 0,
                            "& .MuiFormControlLabel-label": {
                              width: "100%",
                            },
                          }}
                          control={
                            <Checkbox
                              checked={selectedProducts.includes(price.priceId)}
                              onChange={() =>
                                handleCheckboxChange(price.priceId)
                              }
                              color="primary"
                            />
                          }
                          label={
                            <Grid2 container justifyContent="space-between">
                              <Grid2 size={10}>
                                <Typography variant="body">
                                  {price.metadata.description}
                                </Typography>
                              </Grid2>
                              <Grid2 size={2}>
                                <Typography variant="body1" fontWeight="bold">
                                  ${(price.unitAmount / 100).toFixed(2)}
                                </Typography>
                              </Grid2>
                            </Grid2>
                          }
                        />
                      ))}
                    </div>
                  ))}
                </>
              )}
            </DialogContent>
            <DialogActions
              sx={{
                "& .MuiLoadingButton-loadingIndicator": {
                  position: "relative", // Allows the button to widen based on text
                  whiteSpace: "nowrap", // Prevents text from wrapping to a new line
                },
              }}
            >
              <LoadingButton
                loading={!!apiLoading}
                loadingIndicator={apiLoading}
                onClick={handleSubmit}
                variant="contained"
              >
                Add Client
              </LoadingButton>
              <Button onClick={onClose}>Cancel</Button>
            </DialogActions>
          </>
        )}
      />
    </Dialog>
  );
};

export default AddClient;
